import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export default function SectorMenu() {
    const [ showSector, setShowSector ] = useState({ag:false, water:false, fire:false});
    const [ selectedTab, setSelectedTab ] = useState();
    const [ searchParams ] = useSearchParams();
    const navigate = useNavigate();
     
    const menuItems = [
        {
            value: "ag",
            title: "Agriculture",
            tabs: [
                { label:"Precipitation (SPI)", linkTo:"pcpn", tooltip:"Station Standardized Precipitation Index maps" },
                { label:"Precipitation (% normal)", linkTo:"acispcpn", tooltip:"Gridded percent of normal precipitation maps" },
                { label:"Soil Moisture (SPoRT)", linkTo:"sport",  tooltip:"Soil moisture from the SPoRT model" },
                { label:"Soil Moisture (GRACE)", linkTo:"grace",  tooltip:"Soil moisture from the NASA GRACE model" },
                { label:"Evaporation (EDDI)", linkTo:"eddi", tooltip:"Evaporative Drought Demand Index maps" },
                { label:"Ag Drought Impact Survey", linkTo:"agsurvey",  tooltip:"Link to Vermont Agency of Agriculature, Food and Markets Agriculture Drought Impact Survey", externalLink: true, externalUrl: "https://agriculture.vermont.gov/drought" },
            ]
        },
        {
            value: "water",
            title: "Water Resources",
            tabs: [
                { label:"Streamflow", linkTo:"sf",  tooltip:"USGS streamflow map" },
                { label:"Groundwater", linkTo:"gw",  tooltip:"USGS groundwater map" },
                { label:"Lake Champlain Levels", linkTo:"champlain",  tooltip:"Time series of USGS Lake Champlain lake level" },
                { label:"NWS Snow Information", linkTo:"nwssnow",  tooltip:"Link to Northeast River Forecast Center's snow information page", externalLink: true, externalUrl: "https://www.weather.gov/nerfc/snow" },
            ]
        },
        {
            value: "drinking",
            title: "Drinking Water",
            tabs: [
                { label:"DEC Drought Reporter", linkTo:"vtdec",  tooltip:"Link to VT Department of Environmental Conservations Drought Reporter", externalLink: true, externalUrl: "https://experience.arcgis.com/experience/be35cd1bf2c5407b9576f946f5b2f453" },
                { label:"Public Water Systems", linkTo:"vtpws", tooltip:"Link to VT Department of Environmental Conservation Public Water Systems Drought Reports map", externalLink: true, externalUrl: "https://experience.arcgis.com/experience/1ca6197412c64163937d7fb7cc220582" },
                { label:"Drought and Your Well", linkTo:"vtdoh",  tooltip:"Link to VT Department of Health Drought and Your Well page", externalLink: true, externalUrl: "https://www.healthvermont.gov/environment/drinking-water/drought-and-your-well" },
            ]
        },
        {
            value: "fire",
            title: "Wildfire",
            tabs: [
                { label:"KBDI", linkTo:"kbdi",  tooltip:"Keetch-Byram Drought Index map" },
                { label:"Fire Danger Forecast", linkTo:"firedanger",  tooltip:"VT Fire Danger Forecast map (VT Agency of Natural Resources)"},
                { label:"Eastern Area Fire Outlook", linkTo:"fireea", tooltip:"Eastern Area Fire Environmental Outlook", externalLink: true, externalUrl: "https://storymaps.arcgis.com/stories/392023524a5a4c20a237237b5f935eb0" },
            ]
        },
        {
            value: "health",
            title: "Health",
            tabs: [
                { label:"Drought and Your Health", linkTo:"cdc",  tooltip:"Link to Center for Disease Control's Drought and Your Health Page", externalLink: true, externalUrl: "https://www.cdc.gov/drought-health/about/"},
                { label:"Air Quality Index", linkTo:"airnow",  tooltip:"Link to AirNow's Air Quality Index map", externalLink: true, externalUrl: "https://www.airnow.gov/state/?name=vermont"},
                { label:"Cyanobacteria Tracker", linkTo:"cyanbac", tooltip:"Link to VT Department of Health's Cyanobacteria Tracker map", externalLink: true, externalUrl: "https://www.healthvermont.gov/environment/tracking/cyanobacteria-blue-green-algae-tracker" },
            ]
        },
        {
            value: "impacts",
            title: "Impacts",
            tabs: [
                { label:"CMOR Drought Reporting", linkTo:"cmor",  tooltip:"Link to CMOR (Condition Monitoring Observer Reports) drought impacts reporter", externalLink: true, externalUrl: "https://droughtimpacts.unl.edu/Tools/ConditionMonitoringObservations.aspx" },
                { label:"Drought Impact Reporter", linkTo:"dir",  tooltip:"Link to National Drought Mitigation Center's Drought Impact Reporter Dashboard", externalLink: true, externalUrl: "https://experience.arcgis.com/experience/8eb94624372e4aaca02951530bf5eeb2"},
                { label:"CoCoRaHS Reports", linkTo:"cocoreps",  tooltip:"Link to CoCoRaHS (Community Collaborative Rain, Hail and Snow network) Condition Monitoring Reports", externalLink: true, externalUrl: "https://www.cocorahs.org/ViewData/ListConditionMonitoringReports.aspx"},
            ]
        },
        {
            value: "current",
            title: "Drought Monitor",
            tabs: [
                { label:"USDM Current Map", linkTo:"usdm", tooltip:"US Drought Monitor status map - updated every Thursday morning" },
                { label:"USDM 1-Week Change", linkTo:"usdmchng1", tooltip:"US Drought Monitor map - changes since last week" },
                { label:"USDM 2-Week Change", linkTo:"usdmchng2", tooltip:"US Drought Monitor map - changes since 2 weeks ago" },
                { label:"USDM 4-Week Change", linkTo:"usdmchng4", tooltip:"US Drought Monitor map - changes since 4 weeks ago" },
                { label:"Severity/Coverage Index", linkTo:"usdmdsci", tooltip:"US Drought Monitor - Drought Severity and Coverage Index time series" },
                { label:"Classification Info", linkTo:"usdmabout", tooltip:"Link to background information about USDM drought classification" },
                { label:"Current Drought Reduction", linkTo:"nceiterm", tooltip:"Link to maps of precipitation needed to terminate or ameliorate current drought.", externalLink: true, externalUrl: "https://www.ncei.noaa.gov/access/monitoring/drought-recovery/current/wmp/end-rain/1"}
            ]
        },
    ];

    const toggleShowSector = (e) => {
        // Expand selected sector
        const sector = e.currentTarget.value;
        setShowSector({...showSector, [sector]:!showSector[sector]});
    };

   const linkClick = (linkTab) => {
      // Anchor link opened url. Need to navigate to the requested route.
      navigate(linkTab);
    };

    useEffect(() => {
      // Changes highlighting of selected tab when search params change
      const product = searchParams.get("product");
      setSelectedTab(product);
    }, [searchParams]);
    
    return (
        <>
            {menuItems.map((item, index) => (
                <Box key={index} sx={{border: "0.5px solid #AFE1AF", pb:2}}>
                    <Button variant="text" size="large" value={item.value} onClick={toggleShowSector}>{item.title}</Button>
                    <IconButton
                        color="primary"
                        value={item.value} 
                        onClick={toggleShowSector}
                        edge="start" 
                        tabIndex={-1}
                    >
                        {showSector[item.value] ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                    </IconButton>
                    {showSector[item.value] &&
                        <>
                            {item.tabs.map((tabitem, tabindex) => (
                                <React.Fragment key={tabindex}>
                                    <br />
                                    <Tooltip title={tabitem.tooltip || ""} placement="right" sx={{ zIndex: 1500 }}>  
                                        { !tabitem.externalLink 
                                            ?
                                                <Link 
                                                    component={RouterLink}
                                                    to={"?product=" + tabitem.linkTo} 
                                                    underline="hover"
                                                    tabIndex={0}
                                                    sx={{ pl: 3 }}
                                                >
                                                    <Typography 
                                                        component="span"
                                                        variant="body"
                                                        sx={{ fontFamily: "Arial Narrow, Arial, sans-serif", fontWeight: tabitem.linkTo===selectedTab ? "bold" : "regular" }}
                                                    >
                                                        {tabitem.label}
                                                    </Typography>
                                                </Link>
                                            : 
                                                <Link 
                                                    component="a"
                                                    href={tabitem.externalUrl}
                                                    target="_blank"
                                                    rel="noopener noreferer"
                                                    underline="hover"
                                                    tabIndex={0}
                                                    sx={{ pl: 3 }}
                                                >
                                                    <Typography 
                                                        component="span"
                                                        variant="body"
                                                        onClick={()=>{linkClick("?product=" + tabitem.linkTo)}}
                                                        sx={{ fontFamily: "Arial Narrow, Arial, sans-serif", fontWeight: tabitem.linkTo===selectedTab ? "bold" : "regular" }}
                                                    >
                                                        {tabitem.label}
                                                    </Typography>
                                                </Link>
                                        }
                                    </Tooltip>
                                </React.Fragment>
                            ))}
                        </>
                    }
                </Box>
            ))}
        </>
    );
}
